<template>
  <div>
    <div v-if="loader && localCreatives.length === 0" class="text-center">
      <Loader :size="'big'" />
    </div>
    <template v-else>
      <!-- FILTERS -->
      <div class="d-flex align-items-center mb-4 default-form" v-if="showFilters">
        <span class="me-4">{{ $t('filters') }}</span>
        <div class="me-3">
          <span class="fw-bold form-label me-3">{{ $t('name_or_layout') }}:</span>
          <input
          type="text"
          class="form-control form-control-sm"
          v-model="filters.search"
          />
        </div>
        <div class="">
          <span class="fw-bold form-label me-3">{{ $t('format') }}:</span>
          <div class="custom-select custom-select-sm">
            <select
              v-model="filters.format"
              class="form-control form-control-sm"
            >
              <option value="">{{ $t('all') }}</option>
              <option
                v-for="format in creativeFormats"
                :key="format"
                :value="format"
              >
                {{ format | formatCreativeFormat }}
              </option>
            </select>
          </div>
        </div>
      </div> <!-- END FILTERS -->
      <!-- ACCORDION -->
      <div class="card">
        <div class="card-body">
          <table class="table align-middle table-row-dashed fs-6 gy-3 dataTable">
            <thead class="text-color11 fw-600">
              <tr>
                <th class="ps-5 col-5">{{ $t('name') }}</th>
                <th class="col-2 ps-0">{{ $t('format') }}</th>
                <th class="col-2 ps-0" v-if="format && format === 'YOUTUBE'">Link YouTube</th>
                <th class="col-2 ps-0" v-else>{{ $t('size') }}</th>
                <th class="col-1 ps-0 pe-5">{{ $t('status') }}</th>
              </tr>
            </thead>
          </table>
          <div class="accordion accordion-flush" id="accordionCreatives">
            <div
              class="accordion-item border-bottom border-dashed"
              v-for="(creative, index) in localCreatives"
              :key="index"
            >
              <h2 class="accordion-header">
                <button
                  class="accordion-button collapsed py-3"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#collapseCreative-' + index"
                  aria-expanded="false"
                  :aria-controls="'collapseCreative-' + index"
                >
                  <table class="table align-middle table-row-dashed fs-6 gy-3 dataTable no-footer">
                    <tbody>
                      <tr>
                        <td class="col-5">
                          <a
                            href="#"
                            class="link-color3"
                            v-b-modal="`creative-detail-modal-${creative.id}`"
                            @click.prevent
                          >
                            {{ creative.name }}
                          </a>
                        </td>
                        <td class="col-2">{{ creative.format | formatCreativeFormat }}</td>
                        <td class="col-2" v-if="creative.format !== 'YOUTUBE'">{{ creative.layout }}</td>
                        <td class="col-2" v-else>
                          <a :href="'https://www.youtube.com/watch?v=' + creative.youtube_id" target="_blank">
                            {{ $t('click_here') }}
                          </a>
                        </td>
                        <td class="col-1">
                          {{ creative.status | formatStatus }}
                          <template v-if="creative.status === 'ERROR'">
                            <RetryCreativeCreationOnPlatformButton
                              :creativeId="creative.id"
                              :upsert-error="creative.error_message"
                              @retry-successful="reset"
                              @retry-error="handleRetryError"
                            />
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </button>
              </h2>
              <div
                :id="'collapseCreative-' + index"
                class="accordion-collapse collapse"
                data-bs-parent="#accordionCreatives"
              >
                <div class="accordion-body">
                  <CreativeDetail :creative="creative" />
                </div>
              </div>
            </div>
            <div v-if="!loader && localCreatives.length === 0" class="text-center mt-4">
              <p>{{ $t('no_creatives_found') }}</p>
            </div>
          </div>
          <!--begin::Pagination-->
          <div class="d-flex flex-stack flex-wrap pt-10" v-if="localCreatives.length > 0 && totalItems">
            <div class="fs-6 fw-semibold text-gray-700">
              {{ $t('pagination', { from: from, to: to, totalItems: totalItems }) }}
            </div>
            <b-pagination
              class="custom-pagination"
              v-if="totalPages > 1"
              v-model="page"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              :loading="loader"
              @input="getCreatives"
              hide-goto-end-buttons
            />
          </div>
          <!--end::Pagination-->
        </div>
      </div>
    </template>
  </div>
</template>

<script>

import CreativesService from '@/modules/creatives/services/creatives-service'

import CreativeDetail from '@/components/creatives/CreativeDetail'
import Loader from '@/components/common/Loader/Loader'
import RetryCreativeCreationOnPlatformButton from '@/components/creatives/RetryCreativeCreationOnPlatformButton'

export default {
  name: 'CreativeListTable',
  props: ['advertiserId', 'creatives', 'format', 'lineId', 'reload', 'search', 'showFilters'],
  components: {
    CreativeDetail,
    Loader,
    RetryCreativeCreationOnPlatformButton
  },
  data () {
    return {
      creativeFormats: ['DISPLAY', 'NATIVE_DISPLAY', 'DISPLAY_VIDEO', 'PUSH', 'SEARCH', 'YOUTUBE', 'AUDIO'],
      from: 1,
      itemsPerPage: 0,
      filters: {
        search: '',
        format: ''
      },
      localCreatives: [],
      loader: false,
      page: 1,
      to: 9,
      totalItems: 0,
      totalPages: 0,
      throttleSearch: null
    }
  },
  created () {
    if (this.creatives && !this.advertiserId) {
      this.localCreatives = this.creatives
    } else {
      this.getCreatives()
    }
  },
  watch: {
    reload (newValue) {
      if (newValue) this.reset()
    },
    'filters.search' (newValue) {
      clearTimeout(this.throttleSearch)
      this.page = 1
      this.throttleSearch = setTimeout(this.searchCreatives, 500)
    },
    'filters.format' (newValue) {
      clearTimeout(this.throttleSearch)
      this.page = 1
      this.throttleSearch = setTimeout(this.searchCreatives, 500)
    }
  },
  methods: {
    /**
     * Get creatives
     */
    async getCreatives () {
      if (!this.advertiserId && !this.lineId) return false

      this.loader = true
      try {
        this.localCreatives = []
        const creatives = await CreativesService.getCreatives({
          advertiserId: this.advertiserId,
          page: this.page,
          search: this.filters.search,
          format: this.filters.format
        })

        this.localCreatives = creatives.data
        this.totalItems = creatives.meta.total
        this.totalPages = creatives.meta.last_page
        this.itemsPerPage = creatives.meta.per_page
        this.from = creatives.meta.from
        this.to = creatives.meta.to
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Handle retry error
     */
    handleRetryError (error) {
      error.handleGlobally && error.handleGlobally()
    },
    /**
     * Load more
     */
    loadMore () {
      this.page++
      this.getCreatives()
    },
    /**
     * Reset and reload
     */
    reset () {
      this.page = 1
      this.localCreatives = []
      this.getCreatives()
    },
    /**
     * Get creatives for search
     */
    searchCreatives () {
      this.reset()
    }
  }
}
</script>
